import { useCallback, useEffect, useEffectEvent, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { FileIcon } from '@phosphor-icons/react';
import {
  $createTextNode,
  $getSelection,
  $isRangeSelection,
  BLUR_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  COMMAND_PRIORITY_HIGH,
  KEY_ARROW_DOWN_COMMAND,
  KEY_ARROW_UP_COMMAND,
  KEY_ENTER_COMMAND,
  KEY_ESCAPE_COMMAND,
  KEY_TAB_COMMAND,
  type RangeSelection,
  TextNode,
} from 'lexical';

import { type FileTreeNode, useFileTree } from '@/api-models/sandbox-files';
import { fuzzyMatch } from '@/utils/fuzzy-match';

import { $createFileMentionNode, $isFileMentionNode } from './FileMentionNode';

interface FileCandidate {
  path: string;
  name: string;
  /** Parent directory, shown under the name in the dropdown — empty at the workspace root. */
  dir: string;
}

/** Files only (not directories) — mentioning a whole directory isn't a defined operation yet. */
function flattenFiles(nodes: FileTreeNode[], out: FileCandidate[] = []): FileCandidate[] {
  for (const node of nodes) {
    if (node.is_dir) {
      if (node.children) flattenFiles(node.children, out);
      continue;
    }
    out.push({ path: node.path, name: node.name, dir: node.path.slice(0, -(node.name.length + 1)) });
  }
  return out;
}

const MAX_RESULTS = 50;

/** The `@query` right before the caret, if the caret sits inside one — an `@` preceded by nothing
 * or a space, with no space between it and the caret. `null` selection offsets (a real selection,
 * not a caret) never match: a mention is typed, not the target of a text selection. */
function getMentionRange(
  text: string,
  selection: RangeSelection,
): { content: string; start: number; end: number } | null {
  if (selection.focus.offset !== selection.anchor.offset) return null;

  let atIndex = -1;
  const cursorIndex = selection.focus.offset - 1;
  for (let idx = cursorIndex; idx >= 0; idx--) {
    const char = text[idx];
    if (char === ' ') break;
    if (char === '@') {
      atIndex = idx;
      break;
    }
  }
  if (atIndex < 0) return null;
  if (atIndex !== 0 && text[atIndex - 1] !== ' ') return null;

  return { content: text.slice(atIndex + 1, cursorIndex + 1), start: atIndex, end: cursorIndex };
}

/**
 * Turns `@query` typed in the composer into an inline, atomic reference to a file in the
 * session's sandbox — the same candidate list the Files tab's tree already fetches
 * (`useFileTree`), fuzzy-filtered client-side. No network round trip per keystroke: everything
 * here operates on the tree SWR already keeps warm.
 */
export function FileMentionPlugin({ base }: { base: string }) {
  const [editor] = useLexicalComposerContext();
  const { tree } = useFileTree(base);
  const files = useMemo(() => flattenFiles(tree), [tree]);

  const [query, setQuery] = useState<string | null>(null);
  const [results, setResults] = useState<FileCandidate[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [position, setPosition] = useState<{ x: number; y: number; flipAbove: boolean } | null>(null);
  const isSelectingRef = useRef(false);
  const closeOnBlur = useEffectEvent(() => {
    if (query !== null && !isSelectingRef.current) setQuery(null);
  });

  const updateResults = useCallback(
    (raw: string) => {
      const trimmed = raw.trim();
      const matches = trimmed
        ? files
            .map((f) => ({ f, score: fuzzyMatch(trimmed, f.path) }))
            .filter((x): x is { f: FileCandidate; score: number } => x.score !== null)
            .sort((a, b) => b.score - a.score)
            .map((x) => x.f)
        : files;
      setResults(matches.slice(0, MAX_RESULTS));
      setSelectedIndex(0);
    },
    [files],
  );

  const handleMentionSearch = useCallback(
    (text: string, selection: RangeSelection) => {
      if (isSelectingRef.current) return;

      const range = getMentionRange(text, selection);
      if (!range) {
        setQuery(null);
        return;
      }

      setQuery(range.content);
      updateResults(range.content);

      const domSelection = window.getSelection();
      if (domSelection && domSelection.rangeCount > 0) {
        const rect = domSelection.getRangeAt(0).getBoundingClientRect();
        const margin = 8;
        const bottomSpace = window.innerHeight - rect.bottom;
        const flipAbove = bottomSpace < 260 && rect.top > bottomSpace;
        setPosition({ x: rect.left, y: flipAbove ? rect.top - margin : rect.bottom + margin, flipAbove });
      }
    },
    [updateResults],
  );

  const insertMention = useCallback(
    (file: FileCandidate) => {
      editor.focus(() => {
        editor.update(() => {
          const selection = $getSelection();
          if (!$isRangeSelection(selection)) return;
          const anchorNode = selection.anchor.getNode();
          if (!(anchorNode instanceof TextNode)) return;

          const range = getMentionRange(anchorNode.getTextContent(), selection);
          if (!range) return;

          selection.setTextNodeRange(anchorNode, range.start, anchorNode, range.end + 1);
          selection.deleteCharacter(false);
          selection.insertNodes([$createFileMentionNode(file.path), $createTextNode(' ')]);
        });
      });
      setQuery(null);
    },
    [editor],
  );

  useEffect(
    () =>
      editor.registerCommand(
        BLUR_COMMAND,
        () => {
          closeOnBlur();
          return false;
        },
        COMMAND_PRIORITY_CRITICAL,
      ),
    [editor],
  );

  useEffect(
    () =>
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          const selection = $getSelection();
          if (!$isRangeSelection(selection)) return;
          const anchorNode = selection.anchor.getNode();
          if (anchorNode instanceof TextNode && !$isFileMentionNode(anchorNode)) {
            handleMentionSearch(anchorNode.getTextContent(), selection);
          }
        });
      }),
    [editor, handleMentionSearch],
  );

  useEffect(() => {
    const open = query !== null && results.length > 0;
    return editor.registerCommand(
      KEY_ARROW_DOWN_COMMAND,
      (event) => {
        if (!open) return false;
        event.preventDefault();
        setSelectedIndex((i) => Math.min(i + 1, results.length - 1));
        return true;
      },
      COMMAND_PRIORITY_HIGH,
    );
  }, [editor, query, results.length]);

  useEffect(() => {
    const open = query !== null && results.length > 0;
    return editor.registerCommand(
      KEY_ARROW_UP_COMMAND,
      (event) => {
        if (!open) return false;
        event.preventDefault();
        setSelectedIndex((i) => Math.max(i - 1, 0));
        return true;
      },
      COMMAND_PRIORITY_HIGH,
    );
  }, [editor, query, results.length]);

  useEffect(() => {
    const open = query !== null && results.length > 0;
    const accept = (event: KeyboardEvent | null) => {
      if (!open) return false;
      event?.preventDefault();
      const file = results[selectedIndex];
      if (file) insertMention(file);
      return true;
    };
    return mergeCommands(
      editor.registerCommand(KEY_ENTER_COMMAND, accept, COMMAND_PRIORITY_HIGH),
      editor.registerCommand(KEY_TAB_COMMAND, accept, COMMAND_PRIORITY_HIGH),
    );
  }, [editor, query, results, selectedIndex, insertMention]);

  useEffect(
    () =>
      editor.registerCommand(
        KEY_ESCAPE_COMMAND,
        (event) => {
          if (query === null) return false;
          event.preventDefault();
          setQuery(null);
          return true;
        },
        COMMAND_PRIORITY_HIGH,
      ),
    [editor, query],
  );

  if (query === null || results.length === 0 || position === null) return null;

  return createPortal(
    <div
      className="fixed z-dialog w-72 max-w-[80vw] overflow-hidden rounded-xl border border-line-strong bg-surface-2 py-1 shadow-(--shadow-overlay)"
      style={{ left: position.x, top: position.y, transform: position.flipAbove ? 'translateY(-100%)' : undefined }}
      onMouseDown={() => {
        isSelectingRef.current = true;
      }}
      onMouseUp={() => {
        isSelectingRef.current = false;
      }}
    >
      <div className="px-2.5 pt-1 pb-1.5 text-2xs font-medium text-fg-tertiary">Files</div>
      <div className="max-h-72 overflow-y-auto">
        {results.map((file, index) => (
          <button
            key={file.path}
            type="button"
            onMouseEnter={() => setSelectedIndex(index)}
            onMouseDown={(event) => {
              event.preventDefault();
              insertMention(file);
            }}
            className={`flex w-full items-center gap-2 border-none px-2.5 py-1.5 text-left text-xs ${
              index === selectedIndex ? 'bg-brand-tint text-fg' : 'bg-transparent text-fg-secondary'
            }`}
          >
            <FileIcon size={14} className="shrink-0 text-fg-muted" />
            <span className="flex min-w-0 flex-col">
              <span className="truncate font-medium">{file.name}</span>
              {file.dir && <span className="truncate text-2xs text-fg-tertiary">{file.dir}</span>}
            </span>
          </button>
        ))}
      </div>
    </div>,
    document.body,
  );
}

/** `mergeRegister` inlined for two listeners — pulling in `@lexical/utils` for one helper isn't
 * worth the extra dependency surface. */
function mergeCommands(...unregisters: Array<() => void>): () => void {
  return () => {
    for (const unregister of unregisters) unregister();
  };
}
